import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, TrendingUp, Hash, Tag, Calendar, LogOut } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const [summary, setSummary] = useState(null);
  const [tags, setTags] = useState([]);
  const [hashtags, setHashtags] = useState([]);
  const [queries, setQueries] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [selectedQueries, setSelectedQueries] = useState([]);
  const [timelineData, setTimelineData] = useState({ dates: [], series: [] });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem('session_token');
      window.location.href = '/login';
    } catch (error) {
      console.error('Error logging out:', error);
      localStorage.removeItem('session_token');
      window.location.href = '/login';
    }
  };

  useEffect(() => {
    fetchCurrentUser();
    fetchAnalytics();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  useEffect(() => {
    if (queries.length > 0 && selectedQueries.length === 0) {
      // Auto-select first query on load
      setSelectedQueries([queries[0].query]);
    }
  }, [queries]);

  // Removed auto-fetch on selection change - now manual with Apply button

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [summaryRes, tagsRes, hashtagsRes, queriesRes, timelineRes] = await Promise.all([
        axios.get(`${API}/analytics/summary`, { withCredentials: true }),
        axios.get(`${API}/analytics/tags`, { withCredentials: true }),
        axios.get(`${API}/analytics/hashtags`, { withCredentials: true }),
        axios.get(`${API}/analytics/queries`, { withCredentials: true }),
        axios.get(`${API}/analytics/timeline`, { withCredentials: true })
      ]);

      setSummary(summaryRes.data);
      setTags(tagsRes.data.distribution.slice(0, 10));
      setHashtags(hashtagsRes.data.top_hashtags.slice(0, 15));
      setQueries(queriesRes.data.distribution);
      setTimeline(timelineRes.data.timeline);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimelineData = async () => {
    try {
      const params = { queries: selectedQueries.join(',') };
      
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      console.log('Fetching timeline with params:', params);
      
      const response = await axios.get(`${API}/analytics/timeline`, {
        params,
        withCredentials: true
      });

      console.log('Timeline response:', response.data);

      setTimelineData({
        dates: response.data.dates || [],
        series: response.data.series || []
      });
    } catch (error) {
      console.error('Error fetching timeline data:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const handleQuickDateRange = (range) => {
    const now = new Date();
    let from = '';
    let to = '';

    switch (range) {
      case '2019':
        from = '2019-01-01';
        to = '2019-12-31';
        break;
      case '2020':
        from = '2020-01-01';
        to = '2020-12-31';
        break;
      case '2021':
        from = '2021-01-01';
        to = '2021-12-31';
        break;
      case '2022':
        from = '2022-01-01';
        to = '2022-12-31';
        break;
      case 'last_year':
        from = new Date(now.getFullYear() - 1, 0, 1).toISOString().split('T')[0];
        to = new Date(now.getFullYear() - 1, 11, 31).toISOString().split('T')[0];
        break;
      case 'all':
        from = '';
        to = '';
        break;
      default:
        break;
    }

    setDateFrom(from);
    setDateTo(to);
  };

  // Download functions
  const downloadAsCSV = (data, filename) => {
    if (!data || data.length === 0) {
      alert('No data to download');
      return;
    }

    try {
      // Get headers
      const headers = Object.keys(data[0]);
      
      // Create CSV content
      const csvRows = [];
      
      // Add header row
      csvRows.push(headers.join(','));
      
      // Add data rows
      for (const row of data) {
        const values = headers.map(header => {
          const value = row[header];
          // Handle values that might contain commas or quotes
          if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        });
        csvRows.push(values.join(','));
      }
      
      // Create CSV string
      const csvString = csvRows.join('\n');
      
      // Create blob with UTF-8 BOM for Excel compatibility
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvString], { type: 'text/csv;charset=utf-8;' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      window.URL.revokeObjectURL(url);
      
      console.log(`Downloaded ${filename} successfully`);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Failed to download CSV. Please try again.');
    }
  };

  const handleDownloadTags = () => {
    if (tags.length === 0) {
      alert('No tag data to download');
      return;
    }
    const data = tags.map(t => ({ tag: t.tag, count: t.count }));
    downloadAsCSV(data, 'tag_distribution.csv');
  };

  const handleDownloadHashtags = () => {
    if (hashtags.length === 0) {
      alert('No hashtag data to download');
      return;
    }
    const data = hashtags.map(h => ({ hashtag: h.hashtag, count: h.count }));
    downloadAsCSV(data, 'hashtag_frequency.csv');
  };

  const handleDownloadTimeline = () => {
    if (timelineData.dates.length === 0) {
      alert('No timeline data to download');
      return;
    }
    
    const data = [];
    timelineData.dates.forEach((date, i) => {
      const row = { date };
      timelineData.series.forEach(series => {
        row[series.query] = series.data[i] || 0;
      });
      data.push(row);
    });
    
    downloadAsCSV(data, 'timeline_data.csv');
  };

  const handleDownloadSummary = () => {
    if (!summary) {
      alert('No summary data to download');
      return;
    }
    
    const data = [
      { metric: 'Total Tweets', value: summary.total_tweets },
      { metric: 'Annotated', value: summary.total_annotations },
      { metric: 'Bookmarked', value: summary.total_bookmarked },
      { metric: 'Unique Tags', value: summary.unique_tags }
    ];
    
    downloadAsCSV(data, 'summary_statistics.csv');
  };

  const handleQueryToggle = (query) => {
    setSelectedQueries(prev => {
      if (prev.includes(query)) {
        // Don't allow deselecting if it's the last one
        if (prev.length === 1) return prev;
        return prev.filter(q => q !== query);
      } else {
        return [...prev, query];
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
            <p className="text-sm text-gray-600">Overview of your Twitter discourse analysis</p>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={() => navigate('/viewer')} data-testid="back-to-viewer-button">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Viewer
            </Button>
            {currentUser && (
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-button" className="border-red-300 text-red-600 hover:bg-red-50">
                <LogOut className="w-4 h-4 mr-1" />
                Logout
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Summary Cards */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Overview Statistics</h2>
          {summary && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadSummary}
              data-testid="download-summary-button"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export Summary
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Tweets</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{summary?.total_tweets || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Annotated</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{summary?.total_annotations || 0}</div>
              <p className="text-xs text-gray-500 mt-1">
                {summary?.total_tweets > 0
                  ? `${((summary.total_annotations / summary.total_tweets) * 100).toFixed(1)}% complete`
                  : '0% complete'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Bookmarked</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-yellow-600">{summary?.total_bookmarked || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Unique Tags</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{summary?.unique_tags || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tag Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center">
                  <Tag className="w-5 h-5 mr-2" />
                  Top Tags
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadTags}
                  disabled={tags.length === 0}
                  data-testid="download-tags-button"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  CSV
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tags.length > 0 ? (
                <div className="space-y-3">
                  {tags.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">{item.tag}</span>
                          <span className="text-sm text-gray-500">{item.count}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${(item.count / tags[0].count) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No tags yet</p>
              )}
            </CardContent>
          </Card>

          {/* Hashtag Frequency */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center">
                  <Hash className="w-5 h-5 mr-2" />
                  Top Hashtags
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadHashtags}
                  disabled={hashtags.length === 0}
                  data-testid="download-hashtags-button"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  CSV
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {hashtags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {hashtags.map((item, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="text-sm px-3 py-1"
                      style={{
                        fontSize: `${Math.max(0.75, Math.min(1.25, item.count / hashtags[0].count))}rem`
                      }}
                    >
                      {item.hashtag} ({item.count})
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No hashtags found</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Timeline & Queries */}
        <div className="grid grid-cols-1 gap-6">
          {/* Query Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Select Queries/Hashtags to Compare</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {queries.map((item) => (
                  <button
                    key={item.query}
                    onClick={() => handleQueryToggle(item.query)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      selectedQueries.includes(item.query)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                    data-testid={`query-toggle-${item.query}`}
                  >
                    {item.query} ({item.count})
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Select at least one query. Multiple selections show comparison lines.
              </p>
              <Button
                onClick={fetchTimelineData}
                disabled={selectedQueries.length === 0}
                className="w-full mt-3"
                data-testid="apply-timeline-button"
              >
                Apply Selection
              </Button>
            </CardContent>
          </Card>

          {/* Date Range Filter */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Filter by Date Range</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Quick Presets */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Quick Select:</label>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleQuickDateRange('all')}
                      className={!dateFrom && !dateTo ? 'bg-blue-50 border-blue-500' : ''}
                    >
                      All Time
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleQuickDateRange('2019')}>
                      2019
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleQuickDateRange('2020')}>
                      2020
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleQuickDateRange('2021')}>
                      2021
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleQuickDateRange('2022')}>
                      2022
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleQuickDateRange('last_year')}>
                      Last Year
                    </Button>
                  </div>
                </div>

                {/* Custom Date Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">From:</label>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      data-testid="date-from-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">To:</label>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      data-testid="date-to-input"
                    />
                  </div>
                </div>

                {(dateFrom || dateTo) && (
                  <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
                    <span className="text-sm text-gray-700">
                      📅 Showing: {dateFrom || 'Beginning'} to {dateTo || 'Present'}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setDateFrom('');
                        setDateTo('');
                      }}
                      className="text-xs"
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  Tweet Timeline Comparison
                  {(dateFrom || dateTo) && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      Filtered
                    </Badge>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {timelineData.dates.length > 0 && (
                    <>
                      <span className="text-sm font-normal text-gray-500">
                        {timelineData.dates[0]} to {timelineData.dates[timelineData.dates.length - 1]}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDownloadTimeline}
                        data-testid="download-timeline-button"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        CSV
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.print()}
                        data-testid="print-timeline-button"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Print/Save
                      </Button>
                    </>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {timelineData.dates.length > 0 ? (
                <div className="space-y-4">
                  {/* Legend */}
                  <div className="flex flex-wrap gap-3">
                    {timelineData.series.map((series, idx) => {
                      const colors = ['bg-blue-600', 'bg-green-600', 'bg-purple-600', 'bg-orange-600', 'bg-pink-600'];
                      return (
                        <div key={idx} className="flex items-center space-x-2">
                          <div className={`w-3 h-3 rounded-full ${colors[idx % colors.length]}`}></div>
                          <span className="text-sm text-gray-700">{series.query}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Chart Container */}
                  <div className="relative" style={{ height: '350px' }}>
                    {/* Y-Axis */}
                    <div className="absolute left-0 top-0 bottom-16 w-12 flex flex-col justify-between text-xs text-gray-500 text-right pr-2">
                      {(() => {
                        const allValues = timelineData.series.flatMap(s => s.data);
                        const maxValue = Math.max(...allValues, 1);
                        const steps = 5;
                        return Array.from({ length: steps + 1 }, (_, i) => {
                          const value = Math.round((maxValue * (steps - i)) / steps);
                          return <div key={i}>{value}</div>;
                        });
                      })()}
                    </div>

                    {/* Chart Area */}
                    <div className="absolute left-12 right-0 top-0 bottom-16">
                      <svg width="100%" height="100%" className="overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none">
                        {/* Grid lines */}
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <line
                            key={i}
                            x1="0"
                            y1={i * 20}
                            x2="100"
                            y2={i * 20}
                            stroke="#e5e7eb"
                            strokeWidth="0.3"
                            vectorEffect="non-scaling-stroke"
                          />
                        ))}

                        {/* Data lines */}
                        {timelineData.series.map((series, seriesIdx) => {
                          const colors = ['#2563eb', '#16a34a', '#9333ea', '#ea580c', '#ec4899'];
                          const color = colors[seriesIdx % colors.length];
                          const allValues = timelineData.series.flatMap(s => s.data);
                          const maxValue = Math.max(...allValues, 1);
                          
                          if (series.data.length === 0) return null;
                          
                          const points = series.data.map((value, i) => {
                            const x = series.data.length > 1 ? (i / (series.data.length - 1)) * 100 : 50;
                            const y = 100 - (value / maxValue) * 100;
                            return `${x},${y}`;
                          }).join(' ');

                          return (
                            <g key={seriesIdx}>
                              <polyline
                                points={points}
                                fill="none"
                                stroke={color}
                                strokeWidth="0.8"
                                vectorEffect="non-scaling-stroke"
                              />
                              {/* Add dots at each point with hover effect */}
                              {series.data.map((value, i) => {
                                const x = series.data.length > 1 ? (i / (series.data.length - 1)) * 100 : 50;
                                const y = 100 - (value / maxValue) * 100;
                                return (
                                  <g key={i}>
                                    {/* Larger invisible circle for easier hovering */}
                                    <circle
                                      cx={x}
                                      cy={y}
                                      r="2"
                                      fill="transparent"
                                      vectorEffect="non-scaling-stroke"
                                      className="cursor-pointer"
                                    >
                                      <title>{timelineData.dates[i]}: {value} tweets</title>
                                    </circle>
                                    {/* Visible dot */}
                                    <circle
                                      cx={x}
                                      cy={y}
                                      r="1"
                                      fill={color}
                                      stroke="white"
                                      strokeWidth="0.3"
                                      vectorEffect="non-scaling-stroke"
                                      className="pointer-events-none"
                                    />
                                  </g>
                                );
                              })}
                            </g>
                          );
                        })}
                      </svg>
                    </div>

                    {/* X-Axis - Below the chart */}
                    <div className="absolute left-12 right-0 bottom-0 h-16 flex justify-between text-xs text-gray-500 pt-2 border-t border-gray-200">
                      {(() => {
                        const dates = timelineData.dates;
                        const showEvery = Math.max(1, Math.floor(dates.length / 8));
                        return dates.map((date, i) => {
                          if (i % showEvery === 0 || i === dates.length - 1) {
                            return (
                              <div key={i} className="flex flex-col items-center" style={{ width: '80px' }}>
                                <div className="text-center whitespace-nowrap overflow-hidden text-ellipsis">
                                  {date}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        });
                      })()}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex justify-between text-xs text-gray-500 pt-4 border-t">
                    <span>Total: {timelineData.dates.length} days</span>
                    <span>
                      {timelineData.series.reduce((sum, s) => sum + s.data.reduce((a, b) => a + b, 0), 0)} tweets
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  {selectedQueries.length === 0 ? (
                    <p className="text-gray-500">Select queries above to view timeline</p>
                  ) : (
                    <div>
                      <p className="text-gray-500 mb-2">No data available for selected queries</p>
                      <p className="text-xs text-gray-400">
                        This might be because there are no tweets for the selected date range or queries
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Search Queries Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <TrendingUp className="w-5 h-5 mr-2" />
                All Queries Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              {queries.length > 0 ? (
                <div className="space-y-3">
                  {queries.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm font-medium text-gray-700 truncate">{item.query}</span>
                      <Badge variant="secondary">{item.count}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No query data</p>
              )}
            </CardContent>
          </Card>
        </div>

      </div>
    </div>
  );
};

export default DashboardPage;
